import React, { useMemo } from 'react';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { Accordion, TextFieldForm, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { ContactNumberFormItem } from '../../../../components/form-items/ContactNumberFormItem';
import { useStyles } from './styles';
import {
  CandidateProfileFormData,
  CandidateProfileFormDefaultValues,
  CandidateProfileIsDisableInputs,
} from '../../../../models';
import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import { getIExamMasterCodeSelected } from '../../logic/useCandidateProfileForm';
import ResidentialAddress from './HelperComponents/ResidentialAddress';
import ForeignAddress from './HelperComponents/ForeignAddress';
import HideOrShow from './HelperComponents/HideOrShow';
import {
  getContactDisplayValue,
  getIsPreferredContactText,
} from '../../../../utils/ComponentUtils';
import {ControllerRadioGroupWrapper} from "./HelperComponents/ControllerRadioGroupWrapper";

export interface CandidateProfileContactDetailsProps {
  isEditState: boolean;
  formHook: UseFormReturn<CandidateProfileFormData>;
  isDisableInputs?: CandidateProfileIsDisableInputs;
  isHideExtraFormInfo?: boolean;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  formDefaultValuesViewDetails?: CandidateProfileFormDefaultValues;
}

const CandidateProfileContactDetails = ({
  isEditState,
  formHook,
  isDisableInputs = undefined,
  isHideExtraFormInfo = false,
  formDefaultValues = undefined,
  formDefaultValuesViewDetails = undefined,
}: CandidateProfileContactDetailsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { register, formState: { errors } } = formHook;

  const isShowExtraFormInfo = useMemo(() => !isHideExtraFormInfo && isEditState, [
    isHideExtraFormInfo,
    isEditState,
  ]);

  return (
    <Accordion defaultExpanded header={t('profile->>CreateCandidateForm.ContactInformation')}>
      <HideOrShow
        isHide={!isShowExtraFormInfo}
        Component={
          <Typography variant="subtitle1" className={classes.extraInfoText}>
            Please provide your email address as SEAB may email exam related information to you via
            your email address.
          </Typography>
        }
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        containerClassName={classes.formElementContainer}
        labelText={t('profile->>CreateCandidateForm.Email')}
        id="inputText-candidateProfile-email"
        valueText={formDefaultValuesViewDetails?.email}
        EditComponent={
          <TextFieldForm
            id="inputText-candidateProfile-email"
            form={formHook}
            label={t('profile->>CreateCandidateForm.Email')}
            name="email"
            type="text"
            error={!!errors.email}
            helperText={errors?.email?.message?.toString()}
            fullWidth
            inputProps={{ readOnly: isDisableInputs?.email }}
            isRequired
          />
        }
      />

      <ControllerRadioGroupWrapper
                                   control={formHook?.control}
                                   className={`${classes.groupMultirowContainer} ${classes.responsiveFlexContainer}`}
                                   name="preferredTelephoneOrMobile"
                                   defaultValue={formDefaultValuesViewDetails?.preferredTelephoneOrMobile ?? 'M'}>
        <ToggleDisplayEditFormItem
            id="mobileNo"
            isEditState={isEditState}
            labelText={t('profile->>CreateCandidateForm.Mobile No.')}
            isPreferredLabelText={getIsPreferredContactText(
                'M',
                formDefaultValuesViewDetails?.preferredTelephoneOrMobile
            )}
            valueText={getContactDisplayValue(
                false,
                formDefaultValuesViewDetails?.mobileCountryCode?.codeValueDescription,
                formDefaultValuesViewDetails?.mobileContactNumber
            )}
            containerClassName={classes.groupFormElementContainer}
            EditComponent={
              <ContactNumberFormItem
                  id="candidateProfileMobile"
                  formHook={formHook}
                  label={t('profile->>CreateCandidateForm.Mobile No.')}
                  countryCode={{
                    name: 'mobileCountryCode',
                    error: !!errors.mobileCountryCode,
                    helperText: errors?.mobileCountryCode?.message?.toString(),
                    disabled: isDisableInputs?.mobileCountryCode,
                  }}
                  countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
                  number={{
                    name: 'mobileContactNumber',
                    error: !!errors.mobileContactNumber,
                    helperText: errors?.mobileContactNumber?.message?.toString(),
                    disabled: isDisableInputs?.mobileContactNumber,
                  }}
                  isSkipTransform
                  isRequired
              />
            }
        />
        <HideOrShow
            isHide={!isEditState}
            Component={
              <FormControlLabel
                  value="M"
                  name="preferredTelephoneOrMobile"
                  control={<Radio />}
                  label="Preferred contact"
                  className={classes.formElementContainer}
                  disabled={isDisableInputs?.preferredTelephoneOrMobile}
              />
            }
        />
        <ToggleDisplayEditFormItem
            isEditState={isEditState}
            labelText={t('profile->>CreateCandidateForm.TelephoneNo')}
            isPreferredLabelText={getIsPreferredContactText(
                'T',
                formDefaultValuesViewDetails?.preferredTelephoneOrMobile
            )}
            valueText={getContactDisplayValue(
                false,
                formDefaultValuesViewDetails?.telephoneCountryCode?.codeValueDescription,
                formDefaultValuesViewDetails?.telephoneContactNumber
            )}
            id="candidateProfileTelephone"
            containerClassName={classes.groupFormElementContainer}
            EditComponent={
              <ContactNumberFormItem
                  id="candidateProfileTelephone"
                  formHook={formHook}
                  label={t('profile->>CreateCandidateForm.TelephoneNo')}
                  countryCode={{
                    name: 'telephoneCountryCode',
                    error: !!errors.telephoneCountryCode,
                    helperText: errors?.telephoneCountryCode?.message?.toString(),
                    disabled: isDisableInputs?.telephoneCountryCode,
                  }}
                  countryCodeisOptionEqualToValue={getIExamMasterCodeSelected}
                  number={{
                    name: 'telephoneContactNumber',
                    error: !!errors.telephoneContactNumber,
                    helperText: errors?.telephoneContactNumber?.message?.toString(),
                    disabled: isDisableInputs?.telephoneContactNumber,
                  }}
                  isSkipTransform
                  placeholder="Enter Phone No."
              />
            }
        />
        <HideOrShow
            isHide={!isEditState}
            Component={
              <FormControlLabel
                  value="T"
                  name="preferredTelephoneOrMobile"
                  control={<Radio />}
                  label="Preferred contact"
                  className={classes.formElementContainer}
                  disabled={isDisableInputs?.preferredTelephoneOrMobile}
              />
            }
        />
      </ControllerRadioGroupWrapper>


      <ControllerRadioGroupWrapper
          control={formHook?.control}
          name="preferredAddress"
          defaultValue={formDefaultValues?.preferredAddress ?? 'L'}>
        <ResidentialAddress
            isEditState={isEditState}
            formHook={formHook}
            isDisableInputs={isDisableInputs}
            isHideExtraFormInfo={isHideExtraFormInfo}
            formDefaultValues={formDefaultValues}
            formDefaultValuesViewDetails={formDefaultValuesViewDetails}
        />

        <ForeignAddress
            isEditState={isEditState}
            formHook={formHook}
            isDisableInputs={isDisableInputs}
            formDefaultValues={formDefaultValues}
        />
      </ControllerRadioGroupWrapper>
    </Accordion>
  );
};

export default CandidateProfileContactDetails;
